/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import CreateRecordButton from "../input/CreateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
/**
 * @COMPONENT_NAME MaterialUnitPurpose
 * @DESCRIPTION 자재 단위 용도를 관리하는 화면 컴포넌트
 *
 * @PASSTHROUGH_PROPS
 *   - {Object} schema - 자재 단위 용도 테이블 스키마 정보
 *   - {Object} grid - 자재 단위 용도 그리드 객체 참조
 *   - {Object} select - 선택된 자재 단위 용도 정보
 *   - {Object} api - 자재 단위 용도 API 객체
 *
 * @CALLBACKS
 *   - {Function} handleResize - 화면 크기 변경 시 호출되는 콜백
 *   - {Function} notifySuccess - 성공 알림을 표시하는 콜백
 *   - {Function} notifyFailure - 실패 알림을 표시하는 콜백
 *   - {Function} notifyWarning - 경고 알림을 표시하는 콜백
 *   - {Function} onSuccess - 작업 성공 시 호출되는 콜백
 *   - {Function} onFailure - 작업 실패 시 호출되는 콜백
 *   - {Function} onNotifyWarning - 경고 발생 시 호출되는 콜백
 *   - {Function} onApiInitialized - API 초기화 시 호출되는 콜백
 *   - {Function} onInitialized - 그리드 초기화 시 호출되는 콜백
 */

const MaterialUnitPurpose = () => {
    const table = "MaterialUnitPurpose";
    const view = "VwMaterialUnitPurpose";

    const [schema, setSchema] = useState();
    const [grid, setGrid] = useState();
    const [select, setSelect] = useState();

    const [api, setApi] = useState();

    const { t } = useTranslation();
    const setNotification = useSetRecoilState(notificationState);

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            const handleGridSelection = (grid) => {
                setSelect(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            };

            grid.selectionChanged.addHandler(handleGridSelection);
            grid.itemsSourceChanged.addHandler(handleGridSelection);

            return () => {
                if (grid && !grid.isDestroyed) {
                    grid.selectionChanged.removeHandler(handleGridSelection);
                    grid.itemsSourceChanged.removeHandler(handleGridSelection);
                }
            };
        }
    }, [grid]);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg, position) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <>
            <BasicCrudScreenType
                id={table}
                table={table}
                view={view}
                onInitialized={(grid) => onInitialized(grid)}
                onApiInitialized={(api) => onApiInitialized(api)}
                noCreate
                noEdit
                noDelete
                noExcel
                onAppendButtonInFrontOfSearch={() => (
                    <>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <CreateRecordButton
                                    id={table}
                                    schema={schema}
                                    refresh={api}
                                    onSuccess={(msg) => onSuccess(msg)}
                                    onFailure={onFailure}
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="PATCH">
                                <UpdateRecordButton
                                    id={table}
                                    schema={schema}
                                    grid={grid}
                                    refresh={api}
                                    onSuccess={(msg) => onSuccess(msg)}
                                    onFailure={onFailure}
                                    disabled={select?.length === 0}
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <DeleteRecordButton
                                    id={table}
                                    schema={schema}
                                    refresh={api}
                                    grid={grid}
                                    color="secondary"
                                    disabled={select?.length === 0}
                                    onSuccess={(msg) => onSuccess(msg)}
                                    onFailure={onFailure}
                                    onPartialFailure={(msg) => onNotifyWarning(msg)}
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                    </>
                )}
            />
        </>
    );
};

export default MaterialUnitPurpose;
